import { Button, FormikField, ModalStyle as Modal, toastSuccess } from "@hubrise/react-components"
import type { InputRef } from "@hubrise/react-components"
import { Form, Formik } from "formik"
import type { FormikErrors } from "formik"
import * as React from "react"
import { useTranslation } from "react-i18next"

import { useRenameCatalogMutation } from "shared/hooks/mutations"
import { Editor } from "shared/hubrise/editor"
import { ICatalogSummary } from "shared/hubrise/models"

interface RenameModalContentProps {
  catalogSummary: ICatalogSummary
  catalogSummaries: Array<ICatalogSummary>
  editor?: Editor
  close: () => void
}

const RenameModalContent = ({
  catalogSummary,
  catalogSummaries,
  editor,
  close,
}: RenameModalContentProps): JSX.Element => {
  const { t } = useTranslation()

  const $nameRef = React.useRef<InputRef>(null)

  React.useEffect(() => $nameRef.current?.focus())
  const handleFocus = (): void => $nameRef.current?.select()

  type FormValues = { name: string }
  const initialValues: FormValues = { name: catalogSummary.name }

  const validate = ({ name }: FormValues): FormikErrors<FormValues> => {
    const errors: FormikErrors<FormValues> = {}
    if (name.length === 0) errors.name = t("validations.not_empty")
    if (catalogSummaries.map((s) => s.name).includes(name) && name !== catalogSummary.name) {
      errors.name = t("validations.must_be_unique")
    }
    return errors
  }

  const mutation = useRenameCatalogMutation({
    onSuccess: (_, { name }) => {
      toastSuccess(t("catalogs.actions.rename.success", { name }))
    },
  })

  const handleSubmit = ({ name }: FormValues) => {
    if (name !== catalogSummary.name) {
      editor?.rename(name)
      mutation.mutate({ name, catalogHrId: catalogSummary.id })
    }
    close()
  }

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} validate={validate}>
      <Form>
        <Modal.Header>
          <Modal.Title>{t("catalogs.actions.rename.title")}</Modal.Title>
          <Modal.Close onClick={close} />
        </Modal.Header>

        <Modal.Body>
          <FormikField.Input ref={$nameRef} name="name" onFocus={handleFocus} />
        </Modal.Body>

        <Modal.Footer>
          <Button variant="primary" type="submit">
            {t("catalogs.actions.rename.action")}
          </Button>
          <Button variant="empty" onClick={close}>
            {t("general.cancel")}
          </Button>
        </Modal.Footer>
      </Form>
    </Formik>
  )
}

export default RenameModalContent
